/**
 * wf-product.js
 * WF Store — Product page JavaScript (Dawn theme override)
 * Vanilla JS, ES2017+, no jQuery.
 *
 * Modules:
 *  1. WFGallery      — image gallery with swipe, keyboard, thumbs, dots
 *  2. WFVariants     — step variant selector with availability states
 *  3. WFStickyATC    — sticky add-to-cart bar via IntersectionObserver
 *  4. WFCart         — AJAX cart (add, drawer open, count update)
 *  5. WFQuantity     — +/− quantity stepper
 *  6. WFAccordion    — smooth open/close animation for <details>
 *  7. init           — DOMContentLoaded bootstrapper
 */

'use strict';

/* ============================================================
   1. WFGallery — image gallery controller
   ============================================================ */
const WFGallery = (() => {

  let state = {
    currentIndex: 0,
    slides: [],
    dots: [],
    thumbs: [],
    track: null,
    totalSlides: 0,
  };

  /**
   * Move gallery to a given slide index.
   * Wraps around (prev from 0 → last, next from last → 0).
   */
  function goToSlide(index) {
    const { slides, dots, thumbs, track } = state;

    if (!slides.length) return;

    // Clamp / wrap index
    let next = index;
    if (next < 0) next = slides.length - 1;
    if (next >= slides.length) next = 0;

    state.currentIndex = next;

    // Translate the track
    track.style.transform = `translateX(-${next * 100}%)`;

    // Update dot active state
    dots.forEach((dot, i) => {
      const active = i === next;
      dot.classList.toggle('wf-gallery__dot--active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    // Update thumbnail active state
    thumbs.forEach((thumb, i) => {
      const active = i === next;
      thumb.classList.toggle('wf-gallery__thumb--active', active);
      thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  /**
   * Set up touch swipe support on the track wrapper.
   */
  function initTouchSwipe(wrapper) {
    let touchStartX = 0;
    let touchStartY = 0;
    let isDragging = false;

    wrapper.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      isDragging = true;
    }, { passive: true });

    wrapper.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      isDragging = false;

      const deltaX = e.changedTouches[0].clientX - touchStartX;
      const deltaY = e.changedTouches[0].clientY - touchStartY;

      // Only trigger swipe if horizontal movement dominates
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
        if (deltaX < 0) {
          // Swipe left → next
          goToSlide(state.currentIndex + 1);
        } else {
          // Swipe right → prev
          goToSlide(state.currentIndex - 1);
        }
      }
    }, { passive: true });
  }

  /**
   * Initialise the gallery.
   */
  function init() {
    const gallery = document.getElementById('wf-gallery');
    if (!gallery) return;

    const track     = document.getElementById('wf-track');
    const prevBtn   = document.getElementById('wf-prev');
    const nextBtn   = document.getElementById('wf-next');
    const dotsEl    = document.getElementById('wf-dots');

    if (!track) return;

    state.track       = track;
    state.slides      = Array.from(track.querySelectorAll('.wf-gallery__slide'));
    state.dots        = dotsEl ? Array.from(dotsEl.querySelectorAll('.wf-gallery__dot')) : [];
    state.thumbs      = Array.from(gallery.querySelectorAll('.wf-gallery__thumb'));
    state.totalSlides = state.slides.length;

    // Hide arrows when only 1 slide
    if (state.totalSlides <= 1) {
      if (prevBtn) prevBtn.style.display = 'none';
      if (nextBtn) nextBtn.style.display = 'none';
    }

    // Arrow button clicks
    if (prevBtn) {
      prevBtn.addEventListener('click', () => goToSlide(state.currentIndex - 1));
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => goToSlide(state.currentIndex + 1));
    }

    // Dot clicks
    state.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.index, 10);
        if (!isNaN(idx)) goToSlide(idx);
      });
    });

    // Thumbnail clicks
    state.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const idx = parseInt(thumb.dataset.index, 10);
        if (!isNaN(idx)) goToSlide(idx);
      });
    });

    // Touch swipe on the track wrapper
    const trackWrap = gallery.querySelector('.wf-gallery__track-wrap');
    if (trackWrap) initTouchSwipe(trackWrap);

    // Keyboard navigation (ArrowLeft / ArrowRight) when gallery is focused
    gallery.setAttribute('tabindex', '0');
    gallery.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  goToSlide(state.currentIndex - 1);
      if (e.key === 'ArrowRight') goToSlide(state.currentIndex + 1);
    });
  }

  return { init, goToSlide, getState: () => state };
})();


/* ============================================================
   2. WFVariants — step variant selector
   ============================================================ */
const WFVariants = (() => {

  // selectedOptions[optionIndex] = selectedValue (string)
  let selectedOptions = {};
  let variants        = [];
  let options         = [];

  /**
   * Find a variant matching all currently selected option values.
   * Shopify variant has option1, option2, option3 properties.
   */
  function findMatchingVariant() {
    return variants.find((v) => {
      return options.every((opt, idx) => {
        const variantVal = v[`option${idx + 1}`];
        return variantVal === selectedOptions[idx];
      });
    }) || null;
  }

  /**
   * Update the price display in #wf-price.
   */
  function updatePrice(variant) {
    const priceEl = document.getElementById('wf-price');
    if (!priceEl || !variant) return;

    const formatMoney = (cents) => {
      // Use Shopify's currency format if available, else simple fallback
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents, window.Shopify.money_format || '${{amount}}');
      }
      return '$' + (cents / 100).toFixed(2);
    };

    const price         = variant.price;
    const comparePrice  = variant.compare_at_price;
    const onSale        = comparePrice && comparePrice > price;

    if (onSale) {
      priceEl.innerHTML = `
        <span class="wf-price__sale">${formatMoney(price)}</span>
        <span class="wf-price__compare">${formatMoney(comparePrice)}</span>
      `;
    } else {
      priceEl.innerHTML = `<span class="wf-price__regular">${formatMoney(price)}</span>`;
    }

    // Update sticky bar price
    const stickyPrice = document.getElementById('wf-sticky-price');
    if (stickyPrice) stickyPrice.textContent = formatMoney(price);
  }

  /**
   * Update the ATC button state and text.
   */
  function updateATC(variant) {
    const btn     = document.getElementById('wf-atc-btn');
    const btnText = document.getElementById('wf-atc-text');
    if (!btn || !btnText) return;

    const formatMoney = (cents) => {
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents, window.Shopify.money_format || '${{amount}}');
      }
      return '$' + (cents / 100).toFixed(2);
    };

    if (!variant || !variant.available) {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btnText.textContent = 'SOLD OUT';
    } else {
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      btnText.innerHTML = `ADD TO CART &bull; ${formatMoney(variant.price)}`;
    }
  }

  /**
   * Mark option values as --unavailable if no available variant
   * has that value given the OTHER currently selected options.
   */
  function updateUnavailableStates() {
    const optionEls = document.querySelectorAll('.wf-option');

    optionEls.forEach((optionEl) => {
      const optionIndex = parseInt(optionEl.dataset.optionIndex, 10);
      const valueBtns   = optionEl.querySelectorAll('.wf-option__value');

      valueBtns.forEach((btn) => {
        const testValue = btn.dataset.value;

        // Build a test selection: use current selections for all OTHER options,
        // then test if testValue for THIS option has any available variant.
        const hasAvailable = variants.some((v) => {
          if (!v.available) return false;
          return options.every((opt, idx) => {
            if (idx === optionIndex) {
              return v[`option${idx + 1}`] === testValue;
            }
            // For other options: use current selection if set
            if (selectedOptions[idx] !== undefined) {
              return v[`option${idx + 1}`] === selectedOptions[idx];
            }
            return true; // No selection yet for this option — don't restrict
          });
        });

        btn.classList.toggle('wf-option__value--unavailable', !hasAvailable);
        if (!hasAvailable) {
          btn.setAttribute('aria-disabled', 'true');
        } else {
          btn.removeAttribute('aria-disabled');
        }
      });
    });
  }

  /**
   * Handle a value button click: update selection, UI, and variant.
   */
  function handleValueClick(btn) {
    const optionIndex = parseInt(btn.dataset.optionIndex, 10);
    const value       = btn.dataset.value;

    if (btn.classList.contains('wf-option__value--unavailable')) return;

    // Update selection state
    selectedOptions[optionIndex] = value;

    // Update selected class within this option group
    const optionEl  = btn.closest('.wf-option');
    const siblings  = optionEl.querySelectorAll('.wf-option__value');
    siblings.forEach((sib) => {
      const active = sib.dataset.value === value;
      sib.classList.toggle('wf-option__value--selected', active);
      sib.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    // Update "CHOOSE A SIZE • Selected" label text
    const selectedSpan = document.getElementById(`wf-selected-${optionIndex}`);
    if (selectedSpan) selectedSpan.textContent = value;

    // Find matching variant
    const match = findMatchingVariant();

    // Update hidden variant ID
    const variantIdInput = document.getElementById('wf-variant-id');
    if (variantIdInput && match) variantIdInput.value = match.id;

    // Update price + ATC
    if (match) {
      updatePrice(match);
      updateATC(match);

      // If this variant has a featured image, jump gallery to it
      if (match.featured_image && match.featured_image.position !== undefined) {
        // Shopify position is 1-indexed
        WFGallery.goToSlide(match.featured_image.position - 1);
      }
    } else {
      updateATC(null); // No matching variant → show sold out
    }

    // Refresh unavailability for all options
    updateUnavailableStates();
  }

  /**
   * Initialise variant selector from window.WFProduct data.
   */
  function init() {
    if (!window.WFProduct || !window.WFProduct.variants || !window.WFProduct.variants.length) return;

    variants = window.WFProduct.variants;
    options  = window.WFProduct.options;

    // Seed selectedOptions from the current variant
    const currentId = window.WFProduct.currentVariantId;
    const current   = variants.find((v) => v.id === currentId);
    if (current) {
      options.forEach((opt, idx) => {
        selectedOptions[idx] = current[`option${idx + 1}`];
      });
    }

    // Bind value button clicks (event delegation on the form)
    const form = document.getElementById('wf-product-form');
    if (!form) return;

    form.addEventListener('click', (e) => {
      const btn = e.target.closest('.wf-option__value');
      if (btn) handleValueClick(btn);
    });

    // Set initial unavailability states
    updateUnavailableStates();
  }

  return { init };
})();


/* ============================================================
   3. WFStickyATC — sticky add-to-cart bar
   ============================================================ */
const WFStickyATC = (() => {

  function init() {
    const mainBtn  = document.getElementById('wf-atc-btn');
    const stickyEl = document.getElementById('wf-sticky-atc');
    const stickyBtn = document.getElementById('wf-sticky-btn');
    const form      = document.getElementById('wf-product-form');

    if (!mainBtn || !stickyEl) return;

    // Use IntersectionObserver to show/hide sticky bar
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Main ATC button is visible → hide sticky bar
            stickyEl.classList.remove('is-visible');
            stickyEl.setAttribute('aria-hidden', 'true');
          } else {
            // Main ATC button scrolled out of view → show sticky bar
            stickyEl.classList.add('is-visible');
            stickyEl.setAttribute('aria-hidden', 'false');
          }
        });
      },
      { threshold: 0 }
    );

    observer.observe(mainBtn);

    // Sticky ATC button → submit the main product form
    if (stickyBtn && form) {
      stickyBtn.addEventListener('click', () => {
        // Scroll to form to show any validation errors (optional UX)
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      });
    }
  }

  return { init };
})();


/* ============================================================
   4. WFCart — AJAX cart operations
   ============================================================ */
const WFCart = (() => {

  /**
   * Add a variant to the cart via Shopify AJAX API.
   * Returns a promise that resolves with the cart JSON.
   */
  async function addToCart(variantId, quantity = 1) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ description: 'Unable to add item to cart.' }));
      throw new Error(errorData.description || 'Unable to add item to cart.');
    }

    return response.json();
  }

  /**
   * Fetch the current cart state and update the header cart count bubble.
   */
  async function fetchCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Update all cart count elements (Dawn uses multiple selectors)
      const countEls = document.querySelectorAll(
        '.cart-count-bubble span[aria-hidden], #cart-icon-bubble .cart-count-bubble span, [data-cart-count]'
      );

      countEls.forEach((el) => {
        el.textContent = cart.item_count;
      });

      // Also dispatch a custom event for any Dawn cart bubble logic
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    } catch (err) {
      // Non-critical — cart count update failure shouldn't block UX
      console.warn('[WFCart] fetchCart failed:', err);
    }
  }

  /**
   * Open the Dawn cart drawer.
   * Uses Dawn's section rendering API to refresh cart content, then opens the drawer.
   */
  async function openCartDrawer(triggerEl) {
    try {
      // Fetch updated cart drawer HTML via Dawn's section rendering
      const sectionsRes = await fetch('/?sections=cart-drawer,cart-icon-bubble');
      if (sectionsRes.ok) {
        const sections = await sectionsRes.json();
        const parser = new DOMParser();

        // Update cart-drawer inner content
        if (sections['cart-drawer']) {
          const parsed = parser.parseFromString(sections['cart-drawer'], 'text/html');
          const newInner = parsed.querySelector('#CartDrawer');
          const existingInner = document.querySelector('#CartDrawer');
          if (newInner && existingInner) existingInner.innerHTML = newInner.innerHTML;
        }

        // Update cart count bubble
        if (sections['cart-icon-bubble']) {
          const parsed = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
          const newBubble = parsed.querySelector('#cart-icon-bubble');
          const existingBubble = document.querySelector('#cart-icon-bubble');
          if (newBubble && existingBubble) existingBubble.innerHTML = newBubble.innerHTML;
        }
      }
    } catch (_) { /* silent — drawer will still try to open */ }

    // Open via Dawn 9+ cart-drawer component
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open(triggerEl || null);
        return;
      }
      cartDrawer.setAttribute('open', '');
      return;
    }

    // Fallback: cart-notification (older Dawn)
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification && typeof cartNotification.renderContents === 'function') {
      cartNotification.renderContents({ items: [] });
      return;
    }

    // Last resort: go to cart page
    window.location.href = '/cart';
  }

  /**
   * Handle the product form submit event.
   * Intercepts default submission, uses AJAX instead.
   */
  function interceptFormSubmit() {
    const form      = document.getElementById('wf-product-form');
    const atcBtn    = document.getElementById('wf-atc-btn');
    const atcText   = document.getElementById('wf-atc-text');
    const atcSpinner = document.getElementById('wf-atc-spinner');
    const atcError  = document.getElementById('wf-atc-error');
    const qtyInput  = document.getElementById('wf-qty-input');
    const variantInput = document.getElementById('wf-variant-id');

    if (!form || !atcBtn) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const variantId = parseInt(variantInput ? variantInput.value : window.WFProduct.currentVariantId, 10);
      const quantity  = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      if (!variantId) return;

      // Show loading state
      atcBtn.disabled = true;
      if (atcText)    atcText.style.display = 'none';
      if (atcSpinner) atcSpinner.style.display = 'block';
      if (atcError) {
        atcError.style.display = 'none';
        atcError.textContent = '';
      }

      try {
        await addToCart(variantId, quantity);
        await fetchCart();
        await openCartDrawer(atcBtn);
      } catch (err) {
        // Show error message
        if (atcError) {
          atcError.textContent = err.message || 'Something went wrong. Please try again.';
          atcError.style.display = 'block';
        }
      } finally {
        // Always restore button state
        atcBtn.disabled = false;
        if (atcText)    atcText.style.display = '';
        if (atcSpinner) atcSpinner.style.display = 'none';
      }
    });
  }

  return { interceptFormSubmit, addToCart, fetchCart };
})();


/* ============================================================
   5. WFQuantity — +/− quantity stepper
   ============================================================ */
const WFQuantity = (() => {

  function init() {
    // Use event delegation on document body
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.wf-qty__btn');
      if (!btn) return;

      const action = btn.dataset.action;
      const input  = btn.closest('.wf-qty__selector')
                       ? btn.closest('.wf-qty__selector').querySelector('.wf-qty__input')
                       : null;

      if (!input) return;

      let current = parseInt(input.value, 10) || 1;
      const min   = parseInt(input.min, 10) || 1;
      const max   = parseInt(input.max, 10) || Infinity;

      if (action === 'inc') {
        current = Math.min(current + 1, max);
      } else if (action === 'dec') {
        current = Math.max(current - 1, min);
      }

      input.value = current;

      // Dispatch change event so any listeners are notified
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  return { init };
})();


/* ============================================================
   6. WFAccordion — animated open/close for <details>
   ============================================================ */
const WFAccordion = (() => {

  /**
   * Animate max-height on .wf-accordion__body for a smooth expand/collapse.
   * The native <details> toggle handles show/hide logic;
   * we just add the transition on top.
   */
  function initAccordion(details) {
    const body = details.querySelector('.wf-accordion__body');
    if (!body) return;

    // Set initial max-height for open accordions
    if (details.open) {
      body.style.maxHeight = body.scrollHeight + 'px';
      body.style.overflow  = 'hidden';
    } else {
      body.style.maxHeight = '0';
      body.style.overflow  = 'hidden';
    }

    body.style.transition = 'max-height 0.28s ease';

    details.addEventListener('toggle', () => {
      if (details.open) {
        // Opening: expand to scrollHeight
        body.style.maxHeight = body.scrollHeight + 'px';

        // After transition, remove max-height cap so content can reflow
        body.addEventListener('transitionend', () => {
          if (details.open) body.style.maxHeight = 'none';
        }, { once: true });
      } else {
        // Closing: first set to current height, then animate to 0
        body.style.maxHeight = body.scrollHeight + 'px';
        requestAnimationFrame(() => {
          body.style.maxHeight = '0';
        });
      }
    });
  }

  function init() {
    document.querySelectorAll('.wf-accordion').forEach((details) => {
      initAccordion(details);
    });
  }

  return { init };
})();


/* ============================================================
   7. init — DOMContentLoaded bootstrapper
   ============================================================ */
document.addEventListener('DOMContentLoaded', () => {
  WFGallery.init();
  WFVariants.init();
  WFStickyATC.init();
  WFCart.interceptFormSubmit();
  WFQuantity.init();
  WFAccordion.init();
});